/**
 * Music Service Tabs Web Component
 *
 * A web component that displays music service options (Spotify and Apple Music)
 * in a tabbed interface with lazy-loading iframes, or as a single iframe
 * when only one service is available.
 */
class MusicServiceTabs extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.intersectionObserver = null;
    this.isLoaded = false;
    this.activeTab = 'spotify';
  }

  connectedCallback() {
    this.render();
    this.setupIntersectionObserver();
  }

  disconnectedCallback() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }
  }

  static get observedAttributes() {
    return ['track-id', 'type', 'width', 'height', 'apple-music-id'];
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue && this.shadowRoot) {
      this.render();
    }
  }

  get trackId() {
    return this.getAttribute('track-id') || '';
  }

  get appleMusicId() {
    return this.getAttribute('apple-music-id') || '';
  }

  get type() {
    return this.getAttribute('type') || 'track';
  }

  get width() {
    return this.getAttribute('width') || 'w-full lg:w-80';
  }

  get height() {
    return this.getAttribute('height') || '152';
  }

  render() {
    if (!this.shadowRoot) {
      return;
    }

    // Check how many services we have
    const hasSpotify = this.trackId && this.trackId.trim() !== '';
    const hasAppleMusic = this.appleMusicId && this.appleMusicId.trim() !== '';
    const serviceCount = [hasSpotify, hasAppleMusic].filter(Boolean).length;

    // If no services, don't render anything
    if (serviceCount === 0) {
      this.shadowRoot.innerHTML = '';

      return;
    }

    // If only one service, show single iframe without tabs
    if (serviceCount === 1) {
      this.renderSingleService(hasSpotify ? 'spotify' : 'apple-music');

      return;
    }

    // Multiple services - show tabs
    this.renderTabs();
  }

  renderSingleService(service) {
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          width: 100%;
        }
        
        .single-service-container {
          position: relative;
          width: 100%;
          min-height: 152px;
          background: #282828;
          border-radius: 8px;
          overflow: hidden;
        }
        
        .loading-spinner {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 40px;
          height: 40px;
          border: 3px solid #535353;
          border-top: 3px solid ${service === 'spotify' ? '#1DB954' : '#fa243c'};
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        
        .music-iframe {
          width: 100%;
          height: 100%;
          border: none;
          border-radius: 8px;
          opacity: 0;
          transition: opacity 0.3s ease-in-out;
        }
        
        .music-iframe.loaded {
          opacity: 1;
        }
        
        @keyframes spin {
          0% { transform: translate(-50%, -50%) rotate(0deg); }
          100% { transform: translate(-50%, -50%) rotate(360deg); }
        }
        
        .error-message {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          color: #ff6b6b;
          text-align: center;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          font-size: 14px;
          max-width: 80%;
        }
        
        .spotiframe {
          height: 100%;
          min-height: 400px;
          width: 100%;
        }
        
        .spotiframe.track {
          height: 20px;
          min-height: 20px;
        }
      </style>
      
      <div class="single-service-container">
        <div class="loading-spinner" id="${service}-spinner"></div>
        <div class="error-message" id="${service}-error" style="display: none;">
          Failed to load ${service === 'spotify' ? 'Spotify' : 'Apple Music'} player
        </div>
        <iframe 
          class="music-iframe ${service === 'spotify' ? 'spotiframe' : ''}" 
          id="${service}-iframe"
          src="about:blank"
          frameborder="0"
          allowtransparency="true"
          allow="encrypted-media">
        </iframe>
      </div>
    `;

    // Load the iframe immediately for single service
    this.loadIframe(service);
  }

  renderTabs() {
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          display: block;
          width: 100%;
        }
        
        .music-tabs-container {
          position: relative;
          width: 100%;
          min-height: 152px;
          background: #282828;
          border-radius: 8px;
          overflow: hidden;
        }
        
        .tabs-header {
          display: flex;
          background: #1a1a1a;
          border-bottom: 1px solid #404040;
        }
        
        .tab-button {
          flex: 1;
          padding: 12px 16px;
          background: transparent;
          border: none;
          color: #b3b3b3;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          position: relative;
        }
        
        .tab-button:hover {
          color: #ffffff;
          background: #2a2a2a;
        }
        
        .tab-button.active {
          color: #ffffff;
          background: #282828;
        }
        
        .tab-button.active::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 2px;
          background: #1DB954;
        }
        
        .tab-button.apple-music.active::after {
          background: #fa243c;
        }
        
        .tab-content {
          position: relative;
          min-height: 120px;
        }
        
        .tab-pane {
          display: none;
          width: 100%;
          height: 100%;
        }
        
        .tab-pane.active {
          display: block;
        }
        
        .loading-spinner {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 40px;
          height: 40px;
          border: 3px solid #535353;
          border-top: 3px solid #1DB954;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        
        .apple-music .loading-spinner {
          border-top-color: #fa243c;
        }
        
        .music-iframe {
          width: 100%;
          height: 100%;
          border: none;
          border-radius: 0 0 8px 8px;
          opacity: 0;
          transition: opacity 0.3s ease-in-out;
        }
        
        .music-iframe.loaded {
          opacity: 1;
        }
        
        @keyframes spin {
          0% { transform: translate(-50%, -50%) rotate(0deg); }
          100% { transform: translate(-50%, -50%) rotate(360deg); }
        }
        
        .error-message {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          color: #ff6b6b;
          text-align: center;
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
          font-size: 14px;
          max-width: 80%;
        }

        .music-iframe,
        .spotiframe {
          height: 100%;
          min-height: 400px;
          width: 100%;
        }

        .music-iframe.track,
        .spotiframe.track {
          height: 20px;
          min-height: 20px;
        }
      </style>
      
      <div class="music-tabs-container">
        <div class="tabs-header">
          <button class="tab-button active" data-tab="spotify">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style="margin-right: 8px; vertical-align: middle;">
              <path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-9.54-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 3.56-1.021 7.56-.6 10.56 1.32.42.18.479.659.359 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.479 12.84c.361.181.54.78.301 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/>
            </svg>
            Spotify
          </button>
          <button class="tab-button apple-music" data-tab="apple-music">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" style="margin-right: 8px; vertical-align: middle;">
              <path d="M23.997 12c0 6.628-5.372 12-11.999 12C5.372 24 0 18.628 0 12S5.372 0 11.998 0C18.626 0 23.997 5.372 23.997 12zM12 3.6c-4.629 0-8.4 3.771-8.4 8.4s3.771 8.4 8.4 8.4 8.4-3.771 8.4-8.4-3.771-8.4-8.4-8.4z"/>
              <path d="M12 6.6c-3.314 0-6 2.686-6 6s2.686 6 6 6 6-2.686 6-6-2.686-6-6-6z"/>
            </svg>
            Apple Music
          </button>
        </div>
        
        <div class="tab-content">
          <!-- Spotify Tab -->
          <div class="tab-pane active" id="spotify-tab">
            <div class="loading-spinner" id="spotify-spinner"></div>
            <div class="error-message" id="spotify-error" style="display: none;">
              Failed to load Spotify player
            </div>
            <iframe 
              class="music-iframe spotiframe" 
              id="spotify-iframe"
              src="about:blank"
              frameborder="0"
              allowtransparency="true"
              allow="encrypted-media">
            </iframe>
          </div>
          
          <!-- Apple Music Tab -->
          <div class="tab-pane" id="apple-music-tab">
            <div class="loading-spinner apple-music" id="apple-spinner"></div>
            <div class="error-message" id="apple-error" style="display: none;">
              Failed to load Apple Music
            </div>
            <iframe 
              class="music-iframe" 
              id="apple-iframe"
              src="about:blank"
              frameborder="0"
              allowtransparency="true">
            </iframe>
          </div>
        </div>
      </div>
    `;

    this.setupTabHandlers();
  }

  setupTabHandlers() {
    const tabButtons = this.shadowRoot.querySelectorAll('.tab-button');

    tabButtons.forEach(button => {
      button.addEventListener('click', () => {
        const targetTab = button.getAttribute('data-tab');

        this.switchTab(targetTab);
      });
    });
  }

  switchTab(tabName) {
    // Update active tab button
    const tabButtons = this.shadowRoot.querySelectorAll('.tab-button');

    tabButtons.forEach(button => {
      button.classList.remove('active');
      if (button.getAttribute('data-tab') === tabName) {
        button.classList.add('active');
      }
    });

    // Update active tab pane
    const tabPanes = this.shadowRoot.querySelectorAll('.tab-pane');

    tabPanes.forEach(pane => {
      pane.classList.remove('active');
      if (pane.id === `${tabName}-tab`) {
        pane.classList.add('active');
      }
    });

    this.activeTab = tabName;

    // Load the iframe for the active tab
    this.loadIframe(tabName);
  }

  loadIframe(service) {
    if (service === 'spotify') {
      this.loadSpotifyIframe();
    } else if (service === 'apple-music') {
      this.loadAppleMusicIframe();
    }
  }

  loadSpotifyIframe() {
    const iframe = this.shadowRoot.getElementById('spotify-iframe');
    const spinner = this.shadowRoot.getElementById('spotify-spinner');
    const error = this.shadowRoot.getElementById('spotify-error');

    if (!iframe || !spinner) {
      return;
    }

    // Set the Spotify embed URL
    iframe.src = `https://open.spotify.com/embed/${this.type}/${this.trackId}`;
    iframe.style.height = this.height + 'px';

    // Handle iframe load
    iframe.onload = () => {
      spinner.style.display = 'none';
      iframe.classList.add('loaded');
    };

    // Handle iframe error
    iframe.onerror = () => {
      spinner.style.display = 'none';
      error.style.display = 'block';
    };

    // Fallback timeout in case onload doesn't fire
    setTimeout(() => {
      if (spinner.style.display !== 'none') {
        spinner.style.display = 'none';
        iframe.classList.add('loaded');
      }
    }, 5000);
  }

  loadAppleMusicIframe() {
    const iframe = this.shadowRoot.getElementById('apple-iframe');
    const spinner = this.shadowRoot.getElementById('apple-spinner');
    const error = this.shadowRoot.getElementById('apple-error');

    if (!iframe || !spinner) {
      return;
    }

    // Set the actual Apple Music embed URL
    iframe.src = `https://embed.music.apple.com/us/album/${this.appleMusicId}`;
    iframe.setAttribute('allow', 'autoplay *; encrypted-media *; fullscreen *; clipboard-write');
    iframe.setAttribute('frameborder', '0');
    iframe.setAttribute(
      'sandbox',
      'allow-forms allow-popups allow-same-origin allow-scripts allow-top-navigation-by-user-activation'
    );
    iframe.style.width = '100%';
    iframe.style.maxWidth = '660px';
    iframe.style.overflow = 'hidden';
    iframe.style.background = 'transparent';

    // Handle iframe load
    iframe.onload = () => {
      spinner.style.display = 'none';
      iframe.classList.add('loaded');
    };

    // Handle iframe error
    iframe.onerror = () => {
      spinner.style.display = 'none';
      error.style.display = 'block';
    };

    // Fallback timeout in case onload doesn't fire
    setTimeout(() => {
      if (spinner.style.display !== 'none') {
        spinner.style.display = 'none';
        iframe.classList.add('loaded');
      }
    }, 5000);
  }

  setupIntersectionObserver() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
    }

    this.intersectionObserver = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !this.isLoaded) {
            this.isLoaded = true;
            // Load the active tab's iframe when it comes into view
            if (this.activeTab) {
              this.loadIframe(this.activeTab);
            }
          }
        });
      },
      {
        rootMargin: '50px',
        threshold: 0.1,
      }
    );

    this.intersectionObserver.observe(this);
  }
}

// Register the custom element
customElements.define('music-service-tabs', MusicServiceTabs);
